import {isFeatureEnabled} from 'feature-flags';
import {Space, Text} from 'platform/foundation';

import {RefObject, useCallback, useEffect, useState} from 'react';
import {FieldValues, useFormState} from 'react-hook-form';
import {BlockerFunction, useBlocker, useInRouterContext, Blocker} from 'react-router-dom';

import {equals, isNotEmpty, not} from 'ramda';

import {Button} from '../../Button/Button';
import {ButtonGroup} from '../../ButtonGroup/ButtonGroup';
import {Dialog} from '../../Dialog/Dialog';
import {useTranslationContext} from '../../TranslationProvider/TranslationContext';
import {useFormContext} from '../hooks/useFormContext';
import {FormControl} from '../types';

interface UnsavedChangesBlockerProps {
  isDisabled: boolean;
  shouldNavigateWithoutBlockerRef: RefObject<boolean>;
}

const UNSAVED_FORMS_FF_V2 = 'core_unsaved_forms_v2';

const UNBLOCKED_STATE: Blocker = {
  state: 'unblocked',
  reset: undefined,
  proceed: undefined,
  location: undefined,
};

export function UnsavedChangesBlocker<TFieldValues extends FieldValues>(
  props: UnsavedChangesBlockerProps
) {
  const t = useTranslationContext();
  const [isSavingChanges, setIsSavingChanges] = useState(false);
  const formApi = useFormContext<TFieldValues>();
  const isWithinRouterContext = useInRouterContext();
  const isDisabled = props.isDisabled || !isWithinRouterContext;

  const {dirtyFields, isSubmitting} = useFormState({control: formApi.control});

  const isDirty = isNotEmpty(dirtyFields);

  const shouldBlock = useCallback<BlockerFunction>(
    ({currentLocation, nextLocation}) =>
      isDirty &&
      isFeatureEnabled(UNSAVED_FORMS_FF_V2) &&
      not(props.isDisabled) &&
      not(props.shouldNavigateWithoutBlockerRef.current) &&
      not(isSubmitting) &&
      not(isSavingChanges) &&
      not(equals(currentLocation, nextLocation)),
    [
      isDirty,
      isSubmitting,
      isSavingChanges,
      props.isDisabled,
      props.shouldNavigateWithoutBlockerRef,
    ]
  );

  const getUnblockedState = () => UNBLOCKED_STATE;
  const useAppropriateBlocker = isDisabled ? getUnblockedState : useBlocker;
  const blocker = useAppropriateBlocker(shouldBlock);

  useEffect(() => {
    if (blocker.state === 'blocked' && not(isDirty) && not(isSubmitting) && not(isSavingChanges)) {
      blocker.reset?.();
    }
  }, [blocker, isDirty, isSubmitting, isSavingChanges]);

  const handleSaveAndContinue = async () => {
    setIsSavingChanges(true);

    try {
      // Disabled restricted syntax for double type assertion of control object.
      // This is actually needed because we are accessing the formApi object via useFormContext (performance reason),
      // but at the same time we need access to our custom form method to be able to submit the changes from the blocker.
      // eslint-disable-next-line no-restricted-syntax
      const castedControl = formApi.control as unknown as FormControl<TFieldValues>;

      // Pass flag to form's onSubmit so it knows we're saving from the blocker.
      // This is needed for cases where we navigate away from submit handler.
      // Without this flag, blocker gets in race condition between blocked navigation and the handler's navigation.
      const success = await castedControl.submitForm?.({isSavingFromBlocker: true});

      if (success) {
        // Form saved successfully. Continue with the originally blocked navigation.
        blocker.proceed?.();
      } else {
        // Validation failed.
        // Close the blocker dialog. Let user to fix the errors.
        blocker.reset?.();
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      const hasInvalidBlockerTransition = /invalid blocker state transition/i.test(errorMessage);

      if (hasInvalidBlockerTransition) {
        throw new Error(
          "Invalid blocker state transition detected. This usually happens when the form's onSubmit handler " +
            'navigates away from the page. To fix this, check if "isSavingFromBlocker" is true in your ' +
            'onSubmit handler and avoid navigation when saving from the blocker dialog. ' +
            'See the JSDoc for "shouldWatchForUnsavedChanges" prop on the Form component for usage examples.'
        );
      }

      throw error;
    } finally {
      setIsSavingChanges(false);
    }
  };

  const handleClose = () => {
    // Cancel the navigation attempt.
    // User stays on form with unsaved changes.
    blocker.reset?.();
  };

  const handleLeave = () => {
    // Continue with navigation without saving changes.
    blocker.proceed?.();
  };

  return (
    <Dialog
      isOpen={blocker.state === 'blocked'}
      onClose={handleClose}
      size="small"
      title={t('general.notifications.unsavedChanges.title')}
      data-testid="formSaver-dialog"
    >
      <Text size="small">{t('general.notifications.unsavedChanges.description')}</Text>

      <Space vertical={4} />

      <ButtonGroup align="right">
        <Button
          isDisabled={isSavingChanges}
          variant="secondary"
          title={t('general.actions.leave')}
          onClick={handleLeave}
          data-testid="formSaverModal-button-goBack"
        />
        <Button
          isLoading={isSavingChanges}
          title={t('general.actions.saveAndContinue')}
          onClick={handleSaveAndContinue}
          data-testid="formSaverModal-button-saveAndContinue"
        />
      </ButtonGroup>
    </Dialog>
  );
}
